import React, { Fragment } from 'react'
import { Timeline, Typography } from 'antd'
import { includes, map, toLower } from 'lodash'

import { EVENT_SUBTYPES, EVENT_TYPES } from '../../common/constants'
import {
  renderSimpleUpdateEvent,
  renderTreeSaveEvent,
} from '../../common/helpers'
import { formatCreatedByText } from '../../common/utils'

import renderIngredientKeywordEvent from './render-ingredient-keyword-event'

const renderIngredientEvent = event => {
  const { type, subtype, original, changes, createdAt, createdBy } = event

  if (type === EVENT_TYPES.SAVE) {
    return renderTreeSaveEvent(event)
  }

  if (subtype === EVENT_SUBTYPES.COMPONENT_DETAILS_CREATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Created component ingredients for {changes.name}:
        </Typography.Text>
        <br />
        {map(changes.componentIngredients, ({ id, type, name }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {name}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.COMPONENT_DETAILS_DELETE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Deleted component ingredients for {original.name}:
        </Typography.Text>
        <br />
        {map(original.componentIngredients, ({ id, type, name }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {name}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.EXTRA_DETAILS_PHRASES_CREATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>Created phrases for {changes.name}:</Typography.Text>
        <br />
        {map(changes.phrases, ({ id, type, phrase }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {phrase}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.EXTRA_DETAILS_PHRASES_DELETE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>Deleted phrases for {original.name}:</Typography.Text>
        <br />
        {map(original.phrases, ({ id, type, phrase }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {phrase}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.EXTRA_DETAILS_INGREDIENTS_CREATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Created extra ingredients for {changes.name}:
        </Typography.Text>
        <br />
        {map(changes.extraIngredients, ({ id, type, name }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {name}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.EXTRA_DETAILS_INGREDIENTS_DELETE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Deleted extra ingredients for {original.name}:
        </Typography.Text>
        <br />
        {map(original.extraIngredients, ({ id, type, name }) => (
          <Fragment key={id}>
            <Typography.Text>
              {type} {name}
            </Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.OVERRIDES_DETAILS_CREATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Created override ingredients for {changes.name}:
        </Typography.Text>
        <br />
        {map(changes.overridesIngredients, ({ id, name }) => (
          <Fragment key={id}>
            <Typography.Text>{name}</Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.OVERRIDES_DETAILS_DELETE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Deleted override ingredients for {original.name}:
        </Typography.Text>
        <br />
        {map(original.overridesIngredients, ({ id, name }) => (
          <Fragment key={id}>
            <Typography.Text>{name}</Typography.Text>
            <br />
          </Fragment>
        ))}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.ALTERNATIVE_INGREDIENTS_DETAILS_CREATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Created alternative ingredients for {changes.name}:
        </Typography.Text>
        <br />
        {map(
          changes.alternativeIngredients,
          ({
            dietId,
            alternativeIngredientId,
            dietName,
            alternativeIngredientName,
          }) => (
            <Fragment key={`${dietId}-${alternativeIngredientId}`}>
              <Typography.Text>
                {alternativeIngredientName} for diet {dietName}
              </Typography.Text>
              <br />
            </Fragment>
          ),
        )}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.ALTERNATIVE_INGREDIENTS_DETAILS_DELETE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          Deleted alternative ingredients for {original.name}:
        </Typography.Text>
        <br />
        {map(
          original.alternativeIngredients,
          ({
            dietId,
            alternativeIngredientId,
            dietName,
            alternativeIngredientName,
          }) => (
            <Fragment key={`${dietId}-${alternativeIngredientId}`}>
              <Typography.Text>
                {alternativeIngredientName} for diet {dietName}
              </Typography.Text>
              <br />
            </Fragment>
          ),
        )}
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  if (includes(toLower(subtype), 'keyword')) {
    return renderIngredientKeywordEvent(event)
  }

  return renderSimpleUpdateEvent(event, 'name')
}

export default renderIngredientEvent
